import "./ContentAreaItem.scss";
import { ContentAreaType } from "/Models/Generated/ContentAreaType";
import { ComponentWrapperProps } from "/Models/Generated/ComponentWrapperProps";
import { RichTextExtensionProps } from "../RichText/RichText";
import * as Components from "/App.Components";
import { IOverrideContentAreaMargin } from "/Models/Generated/IOverrideContentAreaMargin";
import { ReactNode } from "react";
import { createSpreadableBem } from "/Helpers/createBem";

export enum ContentAreaItemMargin {
  SmallOnDesktop = 1,
  SmallOnDesktopAndMobile = 2,
  Sidebar = 3,
  Huge = 4,
  SidebarInsight = 5,
}

export interface Props {
  contentAreaType: ContentAreaType;
  item: ComponentWrapperProps;
  useBlockOPE: boolean;
  richText?: RichTextExtensionProps;
  idx: number;
  margin?: ContentAreaItemMargin;
}

const ContentAreaItem = ({
  item,
  contentAreaType,
  useBlockOPE,
  richText,
  idx,
  margin,
}: Props) => {
  const Comp = (Components as { [index: string]: any })[item?.name ?? ""];
  if (!Comp) return <div key={idx + item.name}>{item.name}</div>;

  const overrideFullWidthMargin =
    (item.component as IOverrideContentAreaMargin)
      ?.overrideContentAreaFullWidthMargin ?? false;

  const Wrap = (children: ReactNode) => {
    if (
      contentAreaType === ContentAreaType.FullWidth &&
      !overrideFullWidthMargin
    )
      return (
        <Components.GridRow>
          <Components.GridPresetWideContent>
            {children}
          </Components.GridPresetWideContent>
        </Components.GridRow>
      );
    return <>{children}</>;
  };

  const bem = createSpreadableBem("content-area-item");
  return (
    <div
      {...bem("", {
        "--full-width":
          contentAreaType === ContentAreaType.FullWidth &&
          !overrideFullWidthMargin,
        "--half-width": contentAreaType === ContentAreaType.HalfWidth,
        "--small-on-desktop-and-mobile-margin":
          margin === ContentAreaItemMargin.SmallOnDesktopAndMobile,
        "--small-on-desktop-margin":
          margin === ContentAreaItemMargin.SmallOnDesktop,
        "--sidebar-margin": margin === ContentAreaItemMargin.Sidebar,
        "--huge-margin": margin === ContentAreaItemMargin.Huge,
        "--sidebar-insight-margin":
          margin === ContentAreaItemMargin.SidebarInsight,
        "--contact-details": item.name === "ContactDetailsBlock",
        "--featured-link": item.name === "FeaturedLinkBlock",
      })}
      data-epi-block-id={useBlockOPE ? item?.blockId : null}
      data-epi-contentarea-item-index={useBlockOPE ? idx : null}
    >
      {Wrap(
        <>
          {item.name === "RichText" && (
            <Comp {...richText} {...item.component} />
          )}
          {item.name !== "RichText" && (
            <Comp {...item.component} {...{ contentAreaType }} />
          )}
        </>,
      )}
    </div>
  );
};

ContentAreaItem.Margin = ContentAreaItemMargin;

export default ContentAreaItem;
